import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { useForm } from '../../hooks/useForm';
import { FormResponseView } from './FormResponseView';
import { buildSubmissionSchema } from '../../schemas';

interface FormPreviewDialogProps {
  formId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Read-only-intent preview for the form's owner — reuses `FormResponseView` in
 * `previewMode` (shows the form regardless of `status`, submit button inert),
 * embedded inside a Dialog instead of a full page. Template previews use the
 * dedicated `TemplatePreviewDialog` instead (flat, disabled fields, gallery
 * footer) — this component is form-only.
 */
export function FormPreviewDialog({ formId, open, onOpenChange }: FormPreviewDialogProps) {
  const { t } = useTranslation('forms');
  const { form, isLoading, isError } = useForm(open ? formId : '');

  const sortedFields = form?.fields.slice().sort((a, b) => a.order - b.order) ?? [];
  const schema = buildSubmissionSchema(sortedFields, form?.emailRequired ?? false);

  const rhfForm = useReactHookForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', fields: {} },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none overflow-y-auto md:h-[85vh] md:max-w-2xl md:rounded-lg"
        data-testid="forms-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('preview.dialogTitle', { title: form?.title ?? '' })}</DialogTitle>
        </DialogHeader>

        <FormResponseView
          testIdPrefix="forms-preview"
          isLoading={isLoading}
          isError={isError}
          form={form}
          submitted={false}
          rhfForm={rhfForm}
          onSubmit={(e) => e.preventDefault()}
          isSubmitting={false}
          previewMode
          embedded
        />
      </DialogContent>
    </Dialog>
  );
}
